import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { Leaf, Users, TrendingUp, ChevronRight, ChevronLeft, TreePine, Droplets, Heart, Building, DollarSign, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

interface ReinvestmentImpactProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ReinvestmentImpact: React.FC<ReinvestmentImpactProps> = ({ onNext, onPrevious }) => {
  const { investment, results } = useCalculator();
  
  // Get total certificates from results if available, otherwise use initial
  const totalCertificates = results?.certificatesSummary.totalCertificates || investment.initialCertificates;
  
  // Scale impact metrics based on total certificates (with reinvestment)
  const scaleFactor = totalCertificates;
  
  const environmentalImpact = {
    co2Reduction: Math.round(2.5 * scaleFactor * 10) / 10,
    waterSaved: Math.round(25000 * scaleFactor),
    landPreserved: Math.round(4000 * scaleFactor)
  };
  
  const socialImpact = {
    jobsCreated: Math.round(0.5 * scaleFactor * 10) / 10,
    familiesSupported: Math.round(1 * scaleFactor),
    communityProjects: Math.round(0.15 * scaleFactor * 10) / 10
  };
  
  const economicImpact = {
    localEconomyBoost: Math.round(0.1 * scaleFactor * 10) / 10,
    taxContribution: Math.round(15000 * scaleFactor),
    sustainableGrowth: Math.round(0.2 * scaleFactor * 10) / 10
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-teal-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Leaf className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Impacto Amplificado</h2>
              <p className="text-green-100">Descubre cómo la reinversión multiplica el impacto positivo de tu inversión</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Reinvestment Explanation */}
          <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-200 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-700" />
              </div>
              <h3 className="text-lg font-semibold text-blue-800">Impacto Amplificado por Reinversión</h3>
            </div>
            <p className="text-blue-700 text-sm">
              Al reinvertir tus utilidades, no solo incrementas tu patrimonio, sino que también multiplicas tu impacto positivo. 
              Con el tiempo, tus certificados iniciales ({investment.initialCertificates}) pueden crecer hasta {totalCertificates} certificados, 
              amplificando significativamente tu contribución ambiental, social y económica.
            </p>
          </div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-8"
          >
            {/* Environmental Impact */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Leaf className="w-5 h-5 text-green-600" />
                <span>Impacto Ambiental</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                      <TreePine className="w-5 h-5 text-green-700" />
                    </div>
                    <h4 className="font-medium text-green-800">Captura de CO₂</h4>
                  </div>
                  <p className="text-3xl font-bold text-green-700 mb-1">{environmentalImpact.co2Reduction}</p>
                  <p className="text-sm text-green-600">toneladas por año</p>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-blue-700" />
                    </div>
                    <h4 className="font-medium text-blue-800">Ahorro de Agua</h4>
                  </div>
                  <p className="text-3xl font-bold text-blue-700 mb-1">{environmentalImpact.waterSaved.toLocaleString()}</p>
                  <p className="text-sm text-blue-600">litros por año</p>
                </div>
                
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-teal-200 rounded-lg flex items-center justify-center">
                      <Leaf className="w-5 h-5 text-teal-700" />
                    </div>
                    <h4 className="font-medium text-teal-800">Tierra Preservada</h4>
                  </div>
                  <p className="text-3xl font-bold text-teal-700 mb-1">{environmentalImpact.landPreserved.toLocaleString()}</p>
                  <p className="text-sm text-teal-600">metros cuadrados</p>
                </div>
              </div>
            </motion.div>
            
            {/* Social Impact */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <span>Impacto Social</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-700" />
                    </div>
                    <h4 className="font-medium text-purple-800">Empleos Generados</h4>
                  </div>
                  <p className="text-3xl font-bold text-purple-700 mb-1">{socialImpact.jobsCreated}</p>
                  <p className="text-sm text-purple-600">empleos directos e indirectos</p>
                </div>
                
                <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-pink-200 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-pink-700" />
                    </div>
                    <h4 className="font-medium text-pink-800">Familias Apoyadas</h4>
                  </div>
                  <p className="text-3xl font-bold text-pink-700 mb-1">{socialImpact.familiesSupported}</p>
                  <p className="text-sm text-pink-600">familias beneficiadas</p>
                </div>
                
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5 text-indigo-700" />
                    </div>
                    <h4 className="font-medium text-indigo-800">Proyectos Comunitarios</h4>
                  </div>
                  <p className="text-3xl font-bold text-indigo-700 mb-1">{socialImpact.communityProjects}</p>
                  <p className="text-sm text-indigo-600">proyectos financiados</p>
                </div>
              </div>
            </motion.div>
            
            {/* Economic Impact */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                <span>Impacto Económico</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-orange-700" />
                    </div>
                    <h4 className="font-medium text-orange-800">Impulso Económico</h4>
                  </div>
                  <p className="text-3xl font-bold text-orange-700 mb-1">{economicImpact.localEconomyBoost}%</p>
                  <p className="text-sm text-orange-600">incremento en economía local</p>
                </div>
                
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-200 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-700" />
                    </div>
                    <h4 className="font-medium text-amber-800">Contribución Fiscal</h4>
                  </div>
                  <p className="text-3xl font-bold text-amber-700 mb-1">{economicImpact.taxContribution.toLocaleString()}</p>
                  <p className="text-sm text-amber-600">MXN en impuestos</p>
                </div>
                
                <div className="bg-gradient-to-br from-lime-50 to-lime-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-lime-200 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-lime-700" />
                    </div>
                    <h4 className="font-medium text-lime-800">Crecimiento Sostenible</h4>
                  </div>
                  <p className="text-3xl font-bold text-lime-700 mb-1">{economicImpact.sustainableGrowth}%</p>
                  <p className="text-sm text-lime-600">tasa de crecimiento sostenible</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentImpact;